import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Input,
  Label,
  Button,
  ComboBox,
  ComboBoxItem,
  DatePicker,
  MultiComboBox,
  MultiComboBoxItem,
  CheckBox
} from "@ui5/webcomponents-react";
import { v4 as uuidv4 } from "uuid";
import UIModel from "../models/UIModel.json"; // Import UIModel
import "@ui5/webcomponents/dist/Assets.js";
import "@ui5/webcomponents-localization/dist/Assets.js"; // CLDR for date components
import localforage from 'localforage';
import { skillsOptions } from '../models/selectionConstants.js'

// Assuming API URL and Key are available via environment variables
const API_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const today = new Date(); // Keep this Date object definition

// Default health data structure for a new member
const defaultHealthData = {
  disease: "",
  disability: "",
  pwdId: "",
  healthInsurance: "",
  vaccineInfo: "",
  ifaIntake: "",
  ifaSource: "",
  contraceptiveUsed: "",
  everPregnant: "",
  lactationStatus: "",
  pregnancyStatus: "",
  prenatalCheckup: "",
  totalPregnancies: "",
  totalMiscarriages: "",
  totalStillbirths: "",
  totalLivingChildren: "",
  withSeniorId: "", // Added for F6
  seniorCitizenIdNo: "", // Added for F6.1
  G17: { primary: '', secondary: '' },
  G18: { status: '', date: '' },
  G19: { status: '', date: '' },
  G20: { primary: '', secondary: '' },
  G21: '',
};

const FamilyDemographic = ({ formData = {}, onFormChange }) => {
  const [entries, setEntries] = useState([]); // Initialize with an empty array
  const debounceRef = useRef(null);

  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState({});
  const [barangays, setBarangays] = useState({});

  const formDataRef = useRef();

  useEffect(() => {
    // Only update entries if formData.familyMembers is different from previous
    if (formDataRef.current !== formData.familyMembers) {
      console.log("formData.familyMembers changed:", formData.familyMembers);
      // Debug: log what is received

      const safeFormData = formData || {};
      const membersSource =
        Array.isArray(safeFormData.familyMembers) && safeFormData.familyMembers.length > 0
          ? safeFormData.familyMembers
          : [];
      let processedMembers = membersSource.map(entry => {
        const id = entry.member_id || entry.id || uuidv4();
        return {
          id,
          firstName: entry.first_name || entry.firstName || "",
          middleName: entry.middle_name || entry.middleName || "",
          lastName: entry.last_name || entry.lastName || "",
          suffix: entry.suffix_name || entry.suffix || "",
          relationship: entry.relationship || "",
          // Always convert status to string for .startsWith to work
          status: entry.status_id !== undefined && entry.status_id !== null
            ? String(entry.status_id)
            : (typeof entry.status === "number" ? String(entry.status) : (entry.status || "")),
          causeOfDeath: entry.cause_death || entry.causeOfDeath || "",
          sex: entry.sex === "Male" ? "M - Male" : entry.sex === "Female" ? "F - Female" : entry.sex || "",
          gender: entry.gender || "",
          birthdate: entry.birthdate ? entry.birthdate.split("T")[0] : (entry.birth_date ? entry.birth_date.split("T")[0] : ""),
          // Initialize birthProvince, birthMunicipality, birthBarangay from birthPlace if possible, or set to empty
          birthPlace: entry.birthProvince + ',' + entry.birthMunicipality + ',' + entry.birthBarangay || "", 
          birthProvince: entry.birthProvince || "",
          birthMunicipality: entry.birthMunicipality || "",
          birthBarangay: entry.birthBarangay || "",
          registeredAtCivilRegistrar: entry.registeredAtCivilRegistrar || entry.registered_civil_registrar || "",
          citizenship: entry.citizenship || "Philippines",
          migrationStatus: entry.migration_status || entry.migrationStatus || "",
          registeredComelecVoter: entry.registered_voter || entry.registeredComelecVoter || "",
          civilStatus: entry.civil_status || entry.civilStatus || "",
          soloParentId: entry.solo_parent_id || entry.soloParentId || "",
          soloParentIdNo: entry.solo_parent_id_no || entry.soloParentIdNo || "",
          religion: entry.religion || "",
          skills: entry.skills || "",
          organizations: entry.organizations || "",
          ethnicity: entry.ethnicity || "",
          schoolingStatus: entry.schooling_status || entry.schoolingStatus || "",
          schoolingReasons: entry.schoolingReasons || [], // Keep as array
          schoolName: entry.school_name || entry.schoolName || "",
          educationalAttainment: entry.educationalAttainment || (entry.level_name ? [entry.level_name] : []), // Keep as array
          degree: entry.degree || "",
          withSeniorId: entry.with_senior_id === 1 ? "1 Yes" : entry.with_senior_id === 0 ? "2 No" : entry.withSeniorId || "",
          seniorCitizenIdNo: entry.senior_citizen_id || entry.seniorCitizenIdNo || "",
          isOFW: entry.is_ofw === 1 ? "1 - Yes" : entry.is_ofw === 0 ? "2 - No" : entry.isOFW || "",
          countryOfWork: entry.country_of_work || entry.countryOfWork || "",
          mainIncomeSource: entry.mainIncomeSource || entry.main_income_source || "",
          otherIncomeSource: entry.other_income_source || entry.otherIncomeSource || "",
          monthlyIncome: entry.monthly_income || entry.monthlyIncome || "",
          classOfWorker: entry.class_of_worker || entry.classOfWorker || "",
          seekingWork: entry.seeking_work === 1 ? "1 - Actively seeking" : entry.seeking_work === 0 ? "2 - Not actively seeking" : entry.seekingWork || "",
          disease: entry.disease || "",
          disability: entry.disability || "",
          pwdId: entry.pwd_id || entry.pwdId || "",
          healthInsurance: entry.health_insurance || entry.healthInsurance || "",
          vaccineInfo: entry.recent_vaccines || entry.vaccineInfo || "",
          ifaIntake: entry.ifa_intake || entry.ifaIntake || "",
          ifaSource: entry.ifa_source || entry.ifaSource || "",
          contraceptiveUsed: entry.contraceptives_used || entry.contraceptiveUsed || "",
          everPregnant: entry.ever_pregnant || entry.everPregnant || "",
          lactationStatus: entry.lactation_status || entry.lactationStatus || "",
          pregnancyStatus: entry.pregnancy_status || entry.pregnancyStatus || "",
          prenatalCheckup: entry.prenatal_checkup || entry.prenatalCheckup || "",
          totalPregnancies: entry.total_pregnancies || entry.totalPregnancies || "",
          totalMiscarriages: entry.total_miscarriages || entry.totalMiscarriages || "",
          totalStillbirths: entry.total_stillbirths || entry.totalStillbirths || "",
          totalLivingChildren: entry.total_living_children || entry.totalLivingChildren || "",
          vaccinesReceivedPrimary: entry.vaccines_received_primary || entry.vaccinesReceivedPrimary || "",
          vaccinesReceivedSecondary: entry.vaccines_received_secondary || entry.vaccinesReceivedSecondary ||  "",
          micronutrientStatus: entry.micronutrient_status || entry.micronutrientStatus ||  "",
          micronutrientDate: entry.micronutrient_date || entry.micronutrientDate ||  "",
          dewormingStatus: entry.deworming_status || entry.dewormingStatus ||  "",
          dewormingDate: entry.deworming_date || entry.dewormingDate ||  "",
          breastfeedingStatus: entry.breastfeeding_status_primary || entry.breastfeedingStatus ||  "",
          breastfeedingDate: entry.breastfeeding_status_secondary || entry.breastfeedingDate ||  "",

          // Ensure G17-G21 are properly initialized, merging with defaultHealthData
          G17: { ...defaultHealthData.G17, ...(entry.G17 || {}) },
          G18: { ...defaultHealthData.G18, ...(entry.G18 || {}) },
          G19: { ...defaultHealthData.G19, ...(entry.G19 || {}) },
          G20: { ...defaultHealthData.G20, ...(entry.G20 || {}) },
          G21: entry.G21 || entry.feeding_status || defaultHealthData.G21,
        };
      });

      if (processedMembers.length === 0) {
        processedMembers.push({
          id: uuidv4(),
          firstName: safeFormData.firstName || "",
          middleName: safeFormData.middleName || "",
          lastName: safeFormData.lastName || "",
          relationship: "9 - Head of the Family",
          citizenship: "Philippines",
          ...defaultHealthData, // Spread default health data for a new head
        });
      } else {
        // Ensure the first member (head) has all necessary fields from defaultHealthData and formData
        processedMembers[0] = {
          ...defaultHealthData, // Start with defaults
          ...processedMembers[0], // Overlay with already mapped data
          firstName: safeFormData.firstName || processedMembers[0].firstName || "",
          middleName: safeFormData.middleName || processedMembers[0].middleName || "",
          lastName: safeFormData.lastName || processedMembers[0].lastName || "",
          relationship: "9 - Head of the Family", // Enforce relationship for the head
          // Ensure G17-G20 for the head are objects, merging with defaults
          G17: { ...defaultHealthData.G17, ...(processedMembers[0].G17 || {}) },
          G18: { ...defaultHealthData.G18, ...(processedMembers[0].G18 || {}) },
          G19: { ...defaultHealthData.G19, ...(processedMembers[0].G19 || {}) },
          G20: { ...defaultHealthData.G20, ...(processedMembers[0].G20 || {}) },
        };
      }
      setEntries(processedMembers);
      // console.log("Processed Members:", processedMembers);
      formDataRef.current = formData.familyMembers;

      // Initialize birth place dropdowns after entries are set
      // This part might need adjustment if birthPlace is a string and needs parsing
      processedMembers.forEach((entry) => {
        if (entry.birthPlace) {
          const parts = entry.birthPlace.split(',').map(p => p.trim());
          
          // Check for exactly 3 parts before proceeding
          if (parts.length === 3) {
            // Use object destructuring for cleaner assignment
            const [birthProvince, birthMunicipality, birthBarangay] = parts;
            
            // Assign the new properties directly to the entry
            Object.assign(entry, {
              birthProvince,
              birthMunicipality,
              birthBarangay,
            });
          }
        }
      });
    }
  }, [formData.familyMembers]); // Watch formData.familyMembers for changes

  useEffect(() => {
    const fetchProvinces = async () => {
      const cacheProvinceKey = 'location_province';
      try {
        const data = await localforage.getItem(cacheProvinceKey);
        setProvinces(data.data);
      } catch (error) {
        console.error("Failed to fetch provinces:", error);
      }
    };
    fetchProvinces();
  }, []);

  const fetchMunicipalities = async (provinceId, index) => {
    const cacheMunicipalityKey = 'location_municipality';
    if (!provinceId) {
      setMunicipalities(prev => ({ ...prev, [index]: [] }));
      setBarangays(prev => ({ ...prev, [index]: [] }));
      return [];
    }
    try {
      const data = await localforage.getItem(cacheMunicipalityKey);
      const filteredData = Array.isArray(data.data)
      ? data.data.find(item => item.province_id === Number(provinceId))?.municipalities
      : [];
      setMunicipalities(prev => ({ ...prev, [index]: filteredData }));
      setBarangays(prev => ({ ...prev, [index]: [] }));
      return filteredData; // Return the fetched data
    } catch (error) {
      console.error("Failed to fetch municipalities:", error);
      return [];
    }
  };

  const fetchBarangays = async (municipalityId, index) => {
    const cacheBarangayKey = 'location_barangay';
    if (!municipalityId) {
      setBarangays(prev => ({ ...prev, [index]: [] }));
      return [];
    }
    try {
      const data = await localforage.getItem(cacheBarangayKey);
      const filteredData = Array.isArray(data.data)
      ? data.data.find(item => item.municipality_id === Number(municipalityId))?.barangays
      : [];
      setBarangays(prev => ({ ...prev, [index]: filteredData }));
      return filteredData; // Return the fetched data
    } catch (error) {
      console.error("Failed to fetch barangays:", error);
      return [];
    }
  };

  useEffect(() => {
    entries.forEach((entry, index) => {
      if (entry.birthProvince) {
        const selectedProvince = provinces.find(p => p.province_id == entry.birthProvince);
        if (selectedProvince) {
          fetchMunicipalities(selectedProvince.province_id, index)
            .then(fetchedMunicipalitiesData => {
              if (entry.birthMunicipality) {
                const selectedMunicipality = fetchedMunicipalitiesData.find(m => m.municipality_id == entry.birthMunicipality);
                
                if (selectedMunicipality) {
                  fetchBarangays(selectedMunicipality.municipality_id, index);
                }
              }
            })
            .catch(error => {
              console.error("Error in fetching municipalities:", error);
            });
        }
      }
    });
  }, [entries, provinces]); 

  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const birthDate = new Date(birthdate);
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculateAgeInMonths = (birthdate) => {
    if (!birthdate) return null;
    const birthDate = new Date(birthdate);
    const months = (today.getFullYear() - birthDate.getFullYear()) * 12 + (today.getMonth() - birthDate.getMonth());
    if (today.getDate() < birthDate.getDate()) {
      return Math.max(0, months - 1);
    }
    return Math.max(0, months);
  };

  const handleAddEntry = useCallback(() => {
    setEntries(prev => {
      const newEntry = {
        id: uuidv4(),
        citizenship: "Philippines",
        ...defaultHealthData,
      };
      // logs for debugging family member addition bug
      // console.log("previous entries:", prev);
      const updated = [...prev, newEntry];
      // console.log("updated entries:", updated);
      if (onFormChange) {
        onFormChange(updated, "familyMembers");
      }
      return updated;
    });
  }, [onFormChange]);

  const handleDeleteEntry = useCallback((index) => {
    setEntries(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (onFormChange) {
        onFormChange("familyMembers", updated);
      }
      return updated;
    });
    setMunicipalities(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
    setBarangays(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  }, [onFormChange]);

  // UPDATE PROBLEM IS HERE:
  const handleInputChange = useCallback((index, field, value, subField = null) => {
    setEntries((prevEntries) => {
      let updatedEntries = [...prevEntries]; // Use prevEntries from setEntries callback
      let entryToUpdate = { ...updatedEntries[index] };

      // Apply the primary change
      if (subField) {
        entryToUpdate[field] = {
          ...(entryToUpdate[field] || {}),
          [subField]: value,
        };
      } else {
        entryToUpdate[field] = value;
      }

      if (field === 'skills') {
        const selectedItems = Array.from(value.detail.items).map((item) => item.text);
        entryToUpdate.skills = selectedItems;
      }

      // If 'withSeniorId' changed, clear 'seniorCitizenIdNo' if 'withSeniorId' is not '1 Yes'
      if (field === 'withSeniorId' && value !== '1 Yes') {
        entryToUpdate.seniorCitizenIdNo = '';
      }

      if (field === 'soloParentId' && value !== '1 Yes') {
        entryToUpdate.soloParentIdNo = ''; 
      }

      // If birthdate changed, clear dependent fields based on the NEW age
      if (field === 'birthdate') {
        const newBirthdate = value;
        const newAge = calculateAge(newBirthdate);
        const newAgeInMonths = calculateAgeInMonths(newBirthdate);
        const isFemale = entryToUpdate.sex === "F - Female"; // Use the potentially updated entry's sex

        // Clear Senior Citizen ID fields if no longer eligible
        if (newAge === null || newAge < 60) {
            entryToUpdate.withSeniorId = ''; // Changed from seniorCitizenId
            entryToUpdate.seniorCitizenIdNo = ''; // Clear the ID number too
        }

        // Clear COMELEC Voter status if no longer eligible
        if (newAge === null || newAge < 18) {
            entryToUpdate.registeredComelecVoter = '';
        }

        // Clear Seeking Work if no longer eligible
        if (newAge === null || newAge < 15) {
            entryToUpdate.seekingWork = '';
        }

        // Clear IFA fields if no longer eligible (Female 10-16)
        if (!isFemale || newAge === null || newAge < 10 || newAge > 16) {
            entryToUpdate.ifaIntake = '';
            entryToUpdate.ifaSource = '';
        }

        // Clear Contraceptive field if no longer eligible (Age 15-49)
        if (newAge === null || newAge < 15 || newAge > 49) {
            entryToUpdate.contraceptiveUsed = '';
        }

        // Clear Pregnancy fields if no longer eligible (Female 15-49)
        if (!isFemale || newAge === null || newAge < 15 || newAge > 49) {
            entryToUpdate.everPregnant = '';
            entryToUpdate.lactationStatus = '';
            entryToUpdate.pregnancyStatus = '';
            entryToUpdate.prenatalCheckup = '';
            entryToUpdate.totalPregnancies = '';
            entryToUpdate.totalMiscarriages = '';
            entryToUpdate.totalStillbirths = '';
            entryToUpdate.totalLivingChildren = '';
        } else if (entryToUpdate.everPregnant === '2 No') {
             // Also clear G10-G16 if 'Ever Pregnant' is 'No' (redundant with conditional rendering but ensures data clean)
            entryToUpdate.lactationStatus = '';
            entryToUpdate.pregnancyStatus = '';
            entryToUpdate.prenatalCheckup = '';
            entryToUpdate.totalPregnancies = '';
            entryToUpdate.totalMiscarriages = '';
            entryToUpdate.totalStillbirths = '';
            entryToUpdate.totalLivingChildren = '';
        }

        // Clear Infant fields if no longer eligible (0-23 months)
        if (newAgeInMonths === null || newAgeInMonths > 23) {
            entryToUpdate.G17 = { ...defaultHealthData.G17 };
            entryToUpdate.G18 = { ...defaultHealthData.G18 }; // Keep default date structure if needed
            entryToUpdate.G19 = { ...defaultHealthData.G19 }; // Keep default date structure if needed
            entryToUpdate.G20 = { ...defaultHealthData.G20 };
            entryToUpdate.G21 = defaultHealthData.G21;
        }
      }

      // Update the entry in the copied array
      updatedEntries[index] = entryToUpdate;
      

      // Debounced update to parent form data
      if (onFormChange) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (index === 0) {
            // Head's data changed: Update both familyMembers and familyDemographics[0]
            const updatedHeadEntry = updatedEntries[0];
            // Prepare the update object for familyDemographics[0] structure
            const headDemographicsUpdate = {
              firstName: updatedHeadEntry.firstName,
              middleName: updatedHeadEntry.middleName,
              lastName: updatedHeadEntry.lastName,
              relationship: updatedHeadEntry.relationship,
              status: updatedHeadEntry.status,
              sex: updatedHeadEntry.sex, // Keep full string here
              gender: updatedHeadEntry.gender,
              birthdate: updatedHeadEntry.birthdate,
              withSeniorId: updatedHeadEntry.withSeniorId,
              seniorCitizenIdNo: updatedHeadEntry.seniorCitizenIdNo,
              // Add all missing fields from entry
              birthProvince: updatedHeadEntry.birthProvince,
              birthMunicipality: updatedHeadEntry.birthMunicipality,
              birthBarangay: updatedHeadEntry.birthBarangay,
              registeredAtCivilRegistrar: updatedHeadEntry.registeredAtCivilRegistrar,
              citizenship: updatedHeadEntry.citizenship,
              migrationStatus: updatedHeadEntry.migrationStatus,
              registeredVoter: updatedHeadEntry.registeredComelecVoter,
              civilStatus: updatedHeadEntry.civilStatus,
              soloParentId: updatedHeadEntry.soloParentId,
              soloParentIdNo:  updatedHeadEntry.soloParentIdNo,
              religion: updatedHeadEntry.religion,
              skills: updatedHeadEntry.skills,
              organizations: updatedHeadEntry.organizations,
              ethnicity: updatedHeadEntry.ethnicity,
              schoolingStatus: updatedHeadEntry.schoolingStatus,
              schoolingReasons: updatedHeadEntry.schoolingReasons,
              schoolName: updatedHeadEntry.schoolName,
              educationalAttainment: updatedHeadEntry.educationalAttainment,
              degree: updatedHeadEntry.degree
            };

            // Create the updated familyDemographics array
            // Use formData from props directly here as it's within the debounced timeout
            const currentFamilyDemographics = formData.familyDemographics || [];
            const newFamilyDemographicsArray = [...currentFamilyDemographics];
            if (newFamilyDemographicsArray.length > 0) {
              newFamilyDemographicsArray[0] = { ...newFamilyDemographicsArray[0], ...headDemographicsUpdate };
            } else {
              newFamilyDemographicsArray.push({
                relationship: "HH Head",
                status: "Present Member",
                ...headDemographicsUpdate
              });
            }

            // Call onFormChange once with batch update
            // console.log('familyMembers:', updatedEntries)
            // console.log('newFamilyDemographicsArray:', newFamilyDemographicsArray)
            // console.log('sending for batch update:')
            onFormChange({
              familyMembers: updatedEntries,
              familyDemographics: newFamilyDemographicsArray
            }, "batchUpdate");

          } else {
            // Non-head member changed: Only update familyMembers
            onFormChange(updatedEntries,"familyMembers");
          }
        }, 300);
      }
      //console.log(updatedEntries)
      return updatedEntries; // Return the updated array for setEntries
    });
  }, [onFormChange, formData.familyDemographics]);

  const handleInfantHealthChange = useCallback((index, field, key, subKeyOrValue, value = undefined) => {
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      const entryToUpdate = { ...updatedEntries[index] };
      const fieldData = entryToUpdate[field] ? { ...entryToUpdate[field] } : {};

      if (field === 'G17') {
        const vaccineData = fieldData[key] ? { ...fieldData[key] } : {};
        vaccineData[subKeyOrValue] = value;
        fieldData[key] = vaccineData;
        //console.log('vaccineData',vaccineData)
        //console.log('fieldData',fieldData)
        if (subKeyOrValue === 'received' && !value) {
            if (key === 'BCG' || key === 'HepatitisB') {
                delete vaccineData.atBirth;
            } else if (key === 'Pentavalent' || key === 'OPV' || key === 'Pneumococcal' || key === 'MMR') {
                const doses = {
                    Pentavalent: ['1.5mos', '2.5mos', '3.5mos'],
                    OPV: ['1.5mos', '2.5mos', '3.5mos'],
                    Pneumococcal: ['1.5mos', '2.5mos', '3.5mos'],
                    MMR: ['9mos', '1year']
                }[key] || [];
                doses.forEach(dose => delete vaccineData[dose]);
            } else if (key === 'Other') {
                delete vaccineData.specify;
            }
            fieldData[key] = vaccineData;
        }
      } 
      else if (field === 'G18' || field === 'G19') {
        if (typeof subKeyOrValue === 'string' && value !== undefined) {
            if (field === 'G19') {
                //console.log("Debug: G19")
                const nutrientData = fieldData[key] ? { ...fieldData[key] } : {};
                nutrientData[subKeyOrValue] = value;
                fieldData[key] = nutrientData;

                if (subKeyOrValue === 'received' && !value) {
                    if (key === 'VitaminA' || key === 'Iron') {
                        delete nutrientData.date;
                    } else if (key === 'IodizedSalt') {
                        delete nutrientData.source;
                        delete nutrientData.sourceOther;
                    }
                    fieldData[key] = nutrientData;
                }

                if (key === 'IodizedSalt' && subKeyOrValue === 'source' && value !== 'Others, specify') {
                    delete nutrientData.sourceOther;
                    fieldData[key] = nutrientData;
                }
            } 
        }
        if(field === 'G18'){
          fieldData[key] = subKeyOrValue;
          if (key === 'status' && subKeyOrValue !== '1 Dewormed') {
              fieldData.date = '';
          }
        }
      } 
      else if (field === 'G20') {
        fieldData[key] = subKeyOrValue;
      }

      entryToUpdate[field] = fieldData;
      updatedEntries[index] = entryToUpdate;
      if (onFormChange) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onFormChange(updatedEntries, "familyMembers");
        }, 300);
      }
      return updatedEntries;
    });
  }, [onFormChange]);

  const handleLocationChange = useCallback((index, field, selectedName) => {
    handleInputChange(index, field, selectedName);

    if (field === 'birthProvince') {
      const selectedProvince = provinces.find(p => p.province_name === selectedName);
      handleInputChange(index, 'birthMunicipality', '');
      handleInputChange(index, 'birthBarangay', '');
      fetchMunicipalities(selectedProvince?.province_id, index);
    } else if (field === 'birthMunicipality') {
      const selectedMunicipality = (municipalities[index] || []).find(m => m.municipality_name === selectedName);
      handleInputChange(index, 'birthBarangay', '');
      fetchBarangays(selectedMunicipality?.municipality_id, index);
    }
  }, [handleInputChange, provinces, municipalities]);

  const renderField = (label, required, children, gridColumn = "span 1") => (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px", gridColumn }}>
      <Label required={required} style={{ fontWeight: "500" }}>{label}</Label>
      {children}
    </div>
  );

  const getUniqueKey = (prefix, value, index) => {
    return `${prefix}-${String(value).replace(/\s+/g, "-")}-${index}`;
  };

  const formatAPIDate = (dateStr) => {
    return dateStr ? new Date(dateStr).toLocaleDateString('en-CA') : '';
  };

  const renderDependentComboBox = (index, entry, field) => {
    const model = UIModel.HealthNutritionModel[field];
    const data = entry[field] || { primary: '', secondary: '' };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <ComboBox
          onChange={(e) => handleInputChange(index, field, e.target.value, 'primary')}
          value={data.primary}
        >
          <ComboBoxItem text="" />
          {Object.keys(model.options).map((opt, i) => (
            <ComboBoxItem key={`${field}-primary-${index}-${i}`} text={opt} />
          ))}
        </ComboBox>

        {data.primary && model.options[data.primary]?.length > 0 && (
          <ComboBox
            onChange={(e) => handleInputChange(index, field, e.target.value, 'secondary')}
            value={data.secondary}
          >
            <ComboBoxItem text="" />
            {model.options[data.primary].map((opt, i) => (
              <ComboBoxItem key={`${field}-secondary-${index}-${i}`} text={opt} />
            ))}
          </ComboBox>
        )}
      </div>
    );
  };

  const renderDewormingStatus = (index, entry) => {
    const data = entry.G18 || { status: '', date: '' };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <ComboBox
          value={data.status}
          onChange={(e) => handleInputChange(index, 'G18', e.target.value, 'status')}
        >
          <ComboBoxItem text="" />
          {UIModel.HealthNutritionModel.G18.options.map((opt, i) => (
            <ComboBoxItem key={`g18-opt-${index}-${i}`} text={opt} />
          ))}
        </ComboBox>

        {data.status === '1 Dewormed' && (
          <DatePicker
            value={data.date || ""}
            onChange={(e) => handleInputChange(index, 'G18', e.target.value, 'date')}
            formatPattern="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
          />
        )}
      </div>
    );
  };

  const renderMicronutrientStatus = (index, entry) => {
    const data = entry.G19 || { status: '', date: '' };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <ComboBox
          value={data.status}
          onChange={(e) => handleInputChange(index, 'G19', e.target.value, 'status')}
        >
          <ComboBoxItem text="" />
          {UIModel.HealthNutritionModel.G19.options.map((opt, i) => (
            <ComboBoxItem key={`g19-opt-${index}-${i}`} text={opt} />
          ))}
        </ComboBox>

        {UIModel.HealthNutritionModel.G19.needsDate.includes(data.status) && (
          <DatePicker
            value={data.date || ""}
            onChange={(e) => handleInputChange(index, 'G19', e.target.value, 'date')}
            formatPattern="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
          />
        )}
      </div>
    );
  };

  // Get today's date in YYYY-MM-DD string format for maxDate prop
  const todayStringForMaxDate = new Date().toLocaleDateString('en-CA'); // Renamed variable

  return (
    <div style={{ padding: "2rem", maxWidth: "1400px", margin: "auto" }}>
      <h2>Step 8: Family Member's Demographic Characteristics</h2>
      {entries.map((entry, index) => {
        const age = calculateAge(entry.birthdate);
        const ageInMonths = calculateAgeInMonths(entry.birthdate);
        const isFemale = entry.sex === "F - Female";
        const isChild0to23Months = ageInMonths !== null && ageInMonths >= 0 && ageInMonths <= 23;
        const isFemale10to16 = isFemale && age !== null && age >= 10 && age <= 16;
        const isAge15to49 = age !== null && age >= 15 && age <= 49; // New condition for G8
        const isFemale15to49 = isFemale && isAge15to49; // Keep for G9-G16
        const isSenior = age !== null && age >= 60; // Added check for senior age

        const currentMunicipalities = municipalities[index] || [];
        const currentBarangays = barangays[index] || [];
        const isHead = index === 0;

        const showG7 = isFemale10to16 && (entry.ifaIntake === '1 Weekly' || entry.ifaIntake === '2 Monthly');
        const showG10toG16 = isFemale15to49 && entry.everPregnant === 'Yes'; // Keep using isFemale15to49
        const showSeniorIdNo = isSenior && entry.withSeniorId === "1 Yes"; // Condition to show F6.1
        const showSoloParentIdNo = entry.soloParentId === "1 Yes";

        return (
          <div
            key={`member-${entry.id}`}
            style={{
              marginBottom: "2rem",
              padding: "1.5rem",
              border: "1px solid #eee",
              borderRadius: "8px",
              backgroundColor: "#f9f9f9"
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>
                Family Member #{index + 1} {isHead ? "(Head of Family)" : ""}
              </h3>
              <Button
                onClick={() => handleDeleteEntry(index)}
                design="Negative"
                disabled={isHead}
              >
                Remove Family Member
              </Button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 24px", alignItems: "start", marginBottom: '1.5rem' }}>
              <div style={{ gridColumn: "1 / -1", marginBottom: '-8px' }}>
                <Label style={{ fontWeight: "bold", fontSize: '1.1em' }}>Demographic Information</Label>
              </div>
              {renderField("First Name", true, <Input value={entry.firstName || ""} onInput={(e) => handleInputChange(index, "firstName", e.target.value.toUpperCase())} disabled={isHead} />)}
              {renderField("Middle Name", false, <Input value={entry.middleName || ""} onInput={(e) => handleInputChange(index, "middleName", e.target.value.toUpperCase())} disabled={isHead} />)}
              {renderField("Last Name", true, <Input value={entry.lastName || ""} onInput={(e) => handleInputChange(index, "lastName", e.target.value.toUpperCase())} disabled={isHead} />)}

              {renderField("F1: Relationship to HH Head", true,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "relationship", e.target.value)}
                  value={entry.relationship || ""}
                  disabled={isHead}
                >
                  {[
                    "1 - Adopted", "2 - Brother/Sister", "3 - Brother/Sister-in-Law", "4 - Child",
                    "5 - Daughter/Son-in-Law", "6 - Employee", "7 - Grandchild", "8 - Grandparent",
                    "9 - Head of the Family", "10 - Housemaid", "11 - Live in/Co-habit", "12 - Niece/Nephew",
                    "13 - Parent", "14 - Parent-in-Law", "15 - Relative", "16 - Spouse",
                    "17 - Step Son/Daughter", "18 - Step Parent", "19 - Student", "20 - Not Related",
                    "Others, Specify"
                  ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("relationship", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F2: Status", true,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "status", e.target.value)}
                  value={entry.status || ""}
                  required
                >
                  {[ "A - Active", "T - Transferred", "D - Died" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("status", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {entry.status?.startsWith("D") ? renderField("Cause of Death", false,
                <Input
                  value={entry.causeOfDeath || ""}
                  onInput={(e) => handleInputChange(index, "causeOfDeath", e.target.value)}
                />
              ) : <div style={{ gridColumn: "span 1" }}></div>}

              {renderField("F3: Sex", true,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "sex", e.target.value)}
                  value={entry.sex || ""}
                  required
                >
                  {[ "F - Female", "M - Male" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("sex", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F4: Gender", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "gender", e.target.value)}
                  value={entry.gender || ""}
                >
                  {[ "L - Lesbian", "G - Gay", "B - Bisexual", "T - Transgender", "Others, specify", "Not Applicable" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("gender", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F5: Birthdate", true,
                <DatePicker
                  value={entry.birthdate || ""}
                  onChange={(e) => handleInputChange(index, "birthdate", e.target.value)}
                  formatPattern="MM/dd/yyyy"
                  maxDate={todayStringForMaxDate} // Use renamed variable
                  required
                />
              )}

              {renderField("F6: With Senior Citizen ID", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "withSeniorId", e.target.value)} // Changed field name
                  value={entry.withSeniorId || ""} // Changed field name
                  disabled={!isSenior} // Enable only if age >= 60
                >
                  {[ "1 Yes", "2 No", "3 Not Applicable" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("seniorCitizen", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {showSeniorIdNo ? renderField("F6.1: Senior Citizen ID No.", true, // Make required if shown
                <Input
                  value={entry.seniorCitizenIdNo || ""}
                  onInput={(e) => handleInputChange(index, "seniorCitizenIdNo", e.target.value)}
                />
              ) : <div style={{ gridColumn: "span 1" }}></div>} {/* Placeholder if F6.1 is hidden */}

              {renderField("F7: Place of Birth", true,
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Province */}
                  <Label style={{ fontWeight: "bold" }}>Province</Label>
                  <select
                    value={entry.birthProvince || ""}
                    onChange={e => {
                      const provinceId = e.target.value;
                      handleInputChange(index, "birthProvince", provinceId);
                      handleInputChange(index, "birthMunicipality", "");
                      handleInputChange(index, "birthBarangay", "");
                      fetchMunicipalities(provinceId, index);
                    }}
                    style={{ width: "100%", padding: "0.5rem" }}
                  >
                    <option value="">Select Province</option>
                    {Array.isArray(provinces) && provinces.length > 0 && provinces.map((province) => (
                      <option key={province.province_id} value={province.province_id}>
                        {province.province_name}
                      </option>
                    ))}
                  </select>
                  {/* Municipality/City */}
                  <Label style={{ fontWeight: "bold" }}>Municipality/City</Label>
                  <select
                    value={entry.birthMunicipality || ""}
                    onChange={e => {
                      const municipalityId = e.target.value;
                      handleInputChange(index, "birthMunicipality", municipalityId);
                      handleInputChange(index, "birthBarangay", "");
                      fetchBarangays(municipalityId, index);
                    }}
                    style={{ width: "100%", padding: "0.5rem" }}
                    disabled={!entry.birthProvince || (municipalities[index] || []).length === 0}
                  >
                    <option value="">Select Municipality/City</option>
                    {(municipalities[index] || []).map((municipality) => (
                      <option key={municipality.municipality_id} value={municipality.municipality_id}>
                        {municipality.municipality_name}
                      </option>
                    ))}
                  </select>
                  {/* Barangay */}
                  {/* <Label style={{ fontWeight: "bold" }}>Barangay</Label>
                  <select
                    value={entry.birthBarangay || ""}
                    onChange={e => {
                      const barangayId = e.target.value;
                      handleInputChange(index, "birthBarangay", barangayId);
                    }}
                    style={{ width: "100%", padding: "0.5rem" }}
                    disabled={!entry.birthMunicipality || (barangays[index] || []).length === 0}
                  >
                    <option value="">Select Barangay</option>
                    {(barangays[index] || []).map((barangay) => (
                      <option key={barangay.barangay_id} value={barangay.barangay_id}>
                        {barangay.barangay_name}
                      </option>
                    ))}
                  </select> */}
                </div>,
                "span 2"
              )}
              <div></div>

              {renderField("F8: Registered Birth at Civil Registrar", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "registeredAtCivilRegistrar", e.target.value)}
                  value={entry.registeredAtCivilRegistrar || ""}
                >
                  <ComboBoxItem text="" />
                  {["Yes - Registered", "No - Not registered"].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("registered", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F9: Migration Status", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "migrationStatus", e.target.value)}
                  value={entry.migrationStatus || ""}
                >
                  <ComboBoxItem text="" />
                  {[
                    "NM - Non-migrant (>5 years)",
                    "M - Migrant (>=6 months)",
                    "T - Transient"
                  ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("migration", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F10: Registered COMELEC Voter", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "registeredComelecVoter", e.target.value)}
                  value={entry.registeredComelecVoter || ""}
                  disabled={age === null || age < 18}
                >
                  <ComboBoxItem text="" />
                  {[
                    "Yes - Registered here", "No - Registered elsewhere",
                    "NR - Not registered", "NA - Not yet voter"
                  ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("comelec", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}

              {renderField("F11: Civil Status", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "civilStatus", e.target.value)}
                  value={entry.civilStatus || ""}
                >
                  {[
                    "M - Married", "S - Single", "NM - Not Married/Common Law",
                    "A - Anulled", "W - Widow/Widower", "LS - Legally Separated", "Sep - Separated", "D - Divorced"
                  ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("civilStatus", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F12: With Solo Parent ID", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "soloParentId", e.target.value)}
                  value={entry.soloParentId || ""}
                >
                  {[ "1 Yes", "2 No", "3 Not Applicable" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("soloParent", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {showSoloParentIdNo ? renderField("F12.1: Solo Parent ID no.", false,
                <Input
                  value={entry.soloParentIdNo || ""}
                  onInput={(e) => handleInputChange(index, "soloParentIdNo", e.target.value)}
                />
              ) : <div style={{ gridColumn: "span 1" }}></div>}
              {renderField("F13: Religion", false,
                <Input
                  value={entry.religion || ""}
                  onInput={(e) => handleInputChange(index, "religion", e.target.value.toUpperCase())}
                />
              )}

              {renderField("F14: Skills", false,
                <MultiComboBox
                  style={{ flex: 2 }}
                  onSelectionChange={(e) =>
                    handleInputChange(index, "skills", e)
                  }
                >
                  {skillsOptions.map((option) => (
                    <MultiComboBoxItem
                      key={option.label}
                      text={option.label}
                      selected={entry.skills ? entry.skills.includes(option.label) : false} 
                    />
                  ))}
                </MultiComboBox>
              )}
              {renderField("F15: Organizations", false,
                <Input
                  placeholder="Ex. 4Ps"
                  value={entry.organizations || ""}
                  onInput={(e) => handleInputChange(index, "organizations", e.target.value)}
                />
              )}
              {renderField("F16: Ethnicity", false,
                <ComboBox
                  placeholder="Select Ethnicity"
                  value={entry.ethnicity || ""}
                  onChange={(e) => handleInputChange(index, "ethnicity", e.target.value)}
                >
                  {(UIModel.ethnicityOptions || []).map((ethnicity, index) => (
                    <ComboBoxItem key={index} text={ethnicity} />
                  ))}
                </ComboBox>
              )}

              {renderField("F16.2: Country of Citizenship", false,
                <ComboBox
                  placeholder="Select Country"
                  value={entry.citizenship || ""}
                  onChange={(e) => handleInputChange(index, "citizenship", e.target.value)}
                >
                  {(UIModel.familyDemographics?.countries || []).map((country) => (
                    <ComboBoxItem key={country.code} text={country.name} />
                  ))}
                </ComboBox>
              )}
              <div></div>
              <div></div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 24px", alignItems: "start", marginBottom: '1.5rem', paddingTop: "16px", borderTop: '1px solid #ddd' }}>
              <div style={{ gridColumn: "1 / -1", marginBottom: '-8px' }}>
                <Label style={{ fontWeight: "bold", fontSize: '1.1em' }}>Education, Employment & Financial Information</Label>
              </div>
              {renderField("F17: Schooling Status", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "schoolingStatus", e.target.value)}
                  value={entry.schoolingStatus || ""}
                >
                  {[ "1 Currently Studying", "2 Not Studying" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("schooling", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("F19: Name of School", false,
                <Input
                  placeholder="Name of school currently enrolled in"
                  value={entry.schoolName || ""}
                  onInput={(e) => handleInputChange(index, "schoolName", e.target.value)}
                  disabled={entry.schoolingStatus !== "1 Currently Studying"}
                />
              )}
              {renderField("F21: Degree/Course", false,
                <Input
                  placeholder="For Graduated/Still Studying"
                  value={entry.degree || ""}
                  onInput={(e) => handleInputChange(index, "degree", e.target.value)}
                />
              )}

              {age !== null && age >= 3 && age <= 24 && entry.schoolingStatus === "2 Not Studying" && (
                renderField("F18: Reason(s) for not attending school (For 3 to 24 years old)", false,
                  <MultiComboBox
                    onSelectionChange={(e) => {
                      const selectedItems = e.detail.items.map(item => item.text);
                      handleInputChange(index, "schoolingReasons", selectedItems);
                    }}
                  >
                    {[
                      "1 Accessibility", "2 Illness", "3 Disability", "4 Pregnancy", "5 Marriage",
                      "6 Cost/Financial", "7 Employment", "8 Finished", "9 Looking for Work",
                      "10 Lack of Interest", "11 Too Young", "12 Bullying", "13 Family Matters",
                      "14 No/Weak Internet", "15 Modular Not Preferred", "16 Problem w/ Requirements",
                      "17 Lack Of Confidence", "18 Oral Health Issue", "Others, Specify"
                    ].map((reason, i) => (
                      <MultiComboBoxItem
                        key={getUniqueKey("reason", reason, i)}
                        text={reason}
                        selected={entry.schoolingReasons?.includes(reason)}
                      />
                    ))}
                  </MultiComboBox>,
                  "1 / -1"
                )
              )}

              {renderField("F20: Educational Attainment", false,
                <ComboBox
                  onChange={(e) => {
                    // Convert single value to array with one item
                    handleInputChange(index, "educationalAttainment", [e.target.value]);
                  }}
                  value={entry.educationalAttainment?.[0] || ""}
                >
                  <ComboBoxItem text="" />
                  {[
                    "NS–No Schooling", "DC–Day Care", "K–Kinder",
                    "E1", "E2", "E3", "E4", "E5", "E6", "Elem Grad", "HS1", "HS2", "HS3", "HS4", "HSG",
                    "JHS7", "JHS8", "JHS9", "JHS10", "JHS Grad",
                    "SHS11", "SHS12", "SHS Grad",
                    "C1", "C2", "C3", "C4", "C5", "C6", "College Grad",
                    "P–Postgraduate", "V–Vocational"
                  ].map((level, i) => (
                    <ComboBoxItem
                      key={getUniqueKey("education", level, i)}
                      text={level}
                    />
                  ))}
                </ComboBox>,
                "1 / -1"
              )}

              {age !== null && age >= 15 ? (
                renderField("F22: Seeking Work (For 15+ & unemployed)", false,
                  <ComboBox
                    onChange={(e) => handleInputChange(index, "seekingWork", e.target.value)}
                    value={entry.seekingWork || ""}
                  >
                    {[ "1 - Actively seeking", "2 - Not actively seeking", "3 - Employed" ].map((opt, i) => (
                      <ComboBoxItem key={getUniqueKey("seekingWork", opt, i)} text={opt} />
                    ))}
                  </ComboBox>
                )
              ) : <div style={{ gridColumn: "span 1" }}></div>}
              {age !== null && age >= 15 ? <div style={{ gridColumn: "span 2" }}></div> : <div style={{ gridColumn: "span 3" }}></div>}
              
              {renderField("F23: Class of Worker", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "classOfWorker", e.target.value)}
                  value={entry.classOfWorker || ""}
                  disabled={entry.seekingWork !== '3 - Employed'}
                >
                  {[
                    "1-Private Household", "2-Private Establishment", "3-Govt/Govt Corp",
                    "4-Self-employed", "5-Employer", "6-Paid Family Farm/Biz",
                    "7-Unpaid Family Farm/Biz", "8-Not Applicable"
                  ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("classWorker", opt, i)} text={opt} />
                  ))}
                </ComboBox>,
                "span 2"
              )}
              <div></div>

              {renderField("F24: OFW", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "isOFW", e.target.value)}
                  value={entry.isOFW || ""}
                  disabled={entry.seekingWork !== '3 - Employed'}
                >
                  {[ "1 - Yes", "2 - No" ].map((opt, i) => (
                    <ComboBoxItem key={getUniqueKey("ofw", opt, i)} text={opt} />
                  ))}
                </ComboBox>
              )}
              {entry.isOFW === "1 - Yes" ? renderField("F25: Country of Work", false,
                <Input
                  placeholder="Write country of work"
                  value={entry.countryOfWork || ""}
                  onInput={(e) => handleInputChange(index, "countryOfWork", e.target.value)}
                />
              ) : <div style={{ gridColumn: "span 1" }}></div>}
              <div></div>

              {renderField("F26-A: Main Sources of Income", false,
                <Input
                  placeholder="Indicate source"
                  value={entry.mainIncomeSource || ""}
                  onInput={(e) => handleInputChange(index, "mainIncomeSource", e.target.value.toUpperCase())}
                />
              )}
              {renderField("F26-B: Other Sources of Income", false,
                <Input
                  placeholder="Indicate other sources"
                  value={entry.otherIncomeSource || ""}
                  onInput={(e) => handleInputChange(index, "otherIncomeSource", e.target.value.toUpperCase())}
                />
              )}
              {renderField("F27: Monthly Income", false,
                <Input
                  type="Number"
                  placeholder="Estimated monthly income"
                  value={entry.monthlyIncome || ""}
                  onInput={(e) => handleInputChange(index, "monthlyIncome", e.target.value)}
                />
              )}
              <div></div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 24px", alignItems: "start", marginBottom: '1.5rem', paddingTop: "16px", borderTop: '1px solid #ddd' }}>
              <div style={{ gridColumn: "1 / -1", marginBottom: '-8px' }}>
                <Label style={{ fontWeight: "bold", fontSize: '1.1em' }}>Health & Nutrition Information</Label>
              </div>

              {renderField("G1: Disease/Illness", false,
                <Input
                  value={entry.disease || ""}
                  onInput={(e) => handleInputChange(index, "disease", e.target.value.toUpperCase())}
                />
              )}
              {renderField("G2: Disability", false,
                <Input
                  value={entry.disability || ""}
                  onInput={(e) => handleInputChange(index, "disability", e.target.value.toUpperCase())}
                />
              )}
              {renderField("G3: PWD ID No.", false,
                <Input
                  value={entry.pwdId || ""}
                  onInput={(e) => handleInputChange(index, "pwdId", e.target.value)}
                  disabled={entry.disability === ''}
                />
              )}

              {renderField("G4: Type of Health Insurance", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "healthInsurance", e.target.value)}
                  value={entry.healthInsurance || ""}
                >
                  {(UIModel.healthInsuranceOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G4-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("G5: Is 0-23 months old?", false,
                <ComboBox
                  value={isChild0to23Months ? "1 Yes" : "No" || ""}
                  disabled={true}
                >
                  {(['1 Yes', '2 No'] || []).map((opt, i) => (
                    <ComboBoxItem key={`G4-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              )}
              {renderField("G6: Vaccines received in last 12 months", false,
                <Input
                  value={entry.vaccineInfo || ""}
                  onInput={(e) => handleInputChange(index, "vaccineInfo", e.target.value.toUpperCase())}
                  placeholder="Specify vaccines"
                  disabled={age < 2 || age === null}
                />
              )}
              <div></div>

              {isFemale10to16 ? renderField("G7: IFA Intake (Girls 10-16)", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "ifaIntake", e.target.value)}
                  value={entry.ifaIntake || ""}
                >
                  {(UIModel.ifaIntakeOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G7-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}
              {showG7 ? renderField("G8: IFA Source", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "ifaSource", e.target.value)}
                  value={entry.ifaSource || ""}
                >
                  {(UIModel.ifaSourceOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G8-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}
              {isAge15to49 ? renderField("G9: Contraceptive(s) Used (15-49 years old)", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "contraceptiveUsed", e.target.value)}
                  value={entry.contraceptiveUsed || ""}
                >
                  {(UIModel.contraceptiveOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G9-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}

              {isFemale15to49 ? renderField("G10: Ever Pregnant? (Women 15-49)", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "everPregnant", e.target.value)}
                  value={entry.everPregnant || ""}
                >
                  {(UIModel.everPregnantOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G10-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}
              {showG10toG16 ? renderField("G11: Lactation Status", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "lactationStatus", e.target.value)}
                  value={entry.lactationStatus || ""}
                >
                  {(UIModel.lactationStatusOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G11-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}
              {showG10toG16 ? renderField("G12: Pregnancy Status", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "pregnancyStatus", e.target.value)}
                  value={entry.pregnancyStatus || ""}
                >
                  {(UIModel.pregnancyStatusOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G12-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}

              {showG10toG16 ? renderField("G13: Prenatal Check-up", false,
                <ComboBox
                  onChange={(e) => handleInputChange(index, "prenatalCheckup", e.target.value)}
                  value={entry.prenatalCheckup || ""}
                >
                  {(UIModel.prenatalCheckupOptions || []).map((opt, i) => (
                    <ComboBoxItem key={`G13-${index}-${i}`} text={opt} />
                  ))}
                </ComboBox>
              ) : <div></div>}
              {showG10toG16 ? renderField("G14: Total Pregnancies", false,
                <Input
                  type="Number"
                  value={entry.totalPregnancies || ""}
                  onInput={(e) => handleInputChange(index, "totalPregnancies", e.target.value)}
                />
              ) : <div></div>}
              {showG10toG16 ? renderField("G15: Total Miscarriages", false,
                <Input
                  type="Number"
                  value={entry.totalMiscarriages || ""}
                  onInput={(e) => handleInputChange(index, "totalMiscarriages", e.target.value)}
                />
              ) : <div></div>}

              {showG10toG16 ? renderField("G16: Total Stillbirths", false,
                <Input
                  type="Number"
                  value={entry.totalStillbirths || ""}
                  onInput={(e) => handleInputChange(index, "totalStillbirths", e.target.value)}
                />
              ) : <div></div>}
              {showG10toG16 ? renderField("G17: Living Children", false,
                <Input
                  type="Number"
                  value={entry.totalLivingChildren || ""}
                  onInput={(e) => handleInputChange(index, "totalLivingChildren", e.target.value)}
                />
              ) : <div></div>}
            </div>

            {isChild0to23Months && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 24px", alignItems: "start", paddingTop: "16px", borderTop: '1px solid #ddd' }}>
                <div style={{ gridColumn: "1 / -1", marginBottom: '-8px' }}>
                  <Label style={{ fontWeight: "bold", fontSize: '1.1em' }}>Infant Health (0-23 Months)</Label>
                </div>

                {/* G17: Vaccines Received */}
                {renderField("G18: Vaccines Received", false, renderVaccinesReceived(index, entry, handleInfantHealthChange), "1 / -1")}

                {/* G18: Deworming Status */}
                {renderField("G19: Deworming Status", false, renderDewormingStatusUpdated(index, entry, handleInfantHealthChange, todayStringForMaxDate))}

                {/* G19: Micronutrient Intake Status */}
                {renderField("G20: Micronutrient Intake Status", false, renderMicronutrientStatusUpdated(index, entry, handleInfantHealthChange, todayStringForMaxDate), "1 / -1")}

                {/* G20: Breastfeeding Status */}
                {renderField("G21: Exclusive Breastfeeding Status", false, renderBreastfeedingStatus(index, entry, handleInfantHealthChange, todayStringForMaxDate), "1 / -1")}

                {/* G21: Feeding Status */}
                {renderField("G22: Feeding Status", false,
                  <ComboBox
                    onChange={(e) => handleInputChange(index, "G21", e.target.value)} // Use handleInputChange for simple field
                    value={entry.G21 || ""}
                  >
                    <ComboBoxItem text="" />
                    {(UIModel.HealthNutritionModel?.G21?.options || []).map((opt, i) => (
                      <ComboBoxItem key={`G22-${index}-${i}`} text={opt} />
                    ))}
                  </ComboBox>
                )}
                {/* Add placeholders if needed to fill grid */}
                 <div></div>
                 <div></div>
              </div>
            )}
          </div>
        );
      })}

      <Button
        onClick={handleAddEntry}
        design="Positive"
        style={{ marginTop: "1rem" }}
      >
        Add Family Member
      </Button>
    </div>
  );
};

// --- Helper Rendering Functions for Infant Health (Updated/New) ---

// G17: Vaccines Received (Updated)
const renderVaccinesReceived = (index, entry, handleInfantHealthChange) => {
    const parsedData = {}
    if(entry.vaccinesReceivedPrimary !== undefined && entry.vaccinesReceivedSecondary !== undefined) {
      const primary = entry.vaccinesReceivedPrimary
      const secondary = entry.vaccinesReceivedSecondary
      const vaccine_id = {
        BCG: 1,
        HepatitisB: 2,
        Pentavalent: 3,
        OPV: 4,
        IPV: 5,
        Pneumococcal: 6,
        MMR: 7,
        Other: 8
      };
      const primaryArray = primary.split(',');
      const secondaryArray = secondary.split(',');
      const vaccineNameById = Object.fromEntries(
        Object.entries(vaccine_id).map(([name, id]) => [id, name])
      );
      primaryArray.forEach((id, index) => {
        const vaccineName = vaccineNameById[parseInt(id, 10)];
        if (vaccineName) {
          const vaccineEntry = { "received": true };
          const secondaryValue = secondaryArray[index];
          if (secondaryValue !== "X") {
            vaccineEntry[secondaryValue] = true;
          }
          parsedData[vaccineName] = vaccineEntry;
        }
      });
    }

    const g17Data = parsedData || entry.G17 || defaultHealthData.G17;
    const renderDoseCheckboxes = (vaccine, doses) => (
      <div style={{ display: 'flex', gap: '10px', marginLeft: '20px', flexWrap: 'wrap' }}>
        {doses.map(dose => (
          <CheckBox
            key={`${vaccine}-${dose}-${index}`}
            text={dose.replace('mos', ' mos').replace('year', ' year')}
            checked={g17Data[vaccine]?.[dose] || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', vaccine, dose, e.target.checked)}
          />
        ))}
      </div>
    );

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {/* BCG */}
        <div>
          <CheckBox
            text="BCG"
            checked={g17Data.BCG?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'BCG', 'received', e.target.checked)}
          />
          {g17Data.BCG?.received && (
            <div style={{ marginLeft: '20px' }}>
              <CheckBox
                text="At Birth?"
                checked={g17Data.BCG?.atBirth || false}
                onChange={(e) => handleInfantHealthChange(index, 'G17', 'BCG', 'atBirth', e.target.checked)}
              />
            </div>
          )}
        </div>

        {/* Hepatitis B */}
        <div>
          <CheckBox
            text="Hepatitis B"
            checked={g17Data.HepatitisB?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'HepatitisB', 'received', e.target.checked)}
          />
          {g17Data.HepatitisB?.received && (
            <div style={{ marginLeft: '20px' }}>
              <CheckBox
                text="At Birth?"
                checked={g17Data.HepatitisB?.atBirth || false}
                onChange={(e) => handleInfantHealthChange(index, 'G17', 'HepatitisB', 'atBirth', e.target.checked)}
              />
            </div>
          )}
        </div>

        {/* Pentavalent */}
        <div>
          <CheckBox
            text="Pentavalent (DPT-Hep & HiB)"
            checked={g17Data.Pentavalent?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'Pentavalent', 'received', e.target.checked)}
          />
          {g17Data.Pentavalent?.received && renderDoseCheckboxes('Pentavalent', ['1.5mos', '2.5mos', '3.5mos'])}
        </div>

        {/* OPV */}
        <div>
          <CheckBox
            text="Oral Polio Vaccine (OPV)"
            checked={g17Data.OPV?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'OPV', 'received', e.target.checked)}
          />
          {g17Data.OPV?.received && renderDoseCheckboxes('OPV', ['1.5mos', '2.5mos', '3.5mos'])}
        </div>

        {/* IPV */}
        <div>
          <CheckBox
            text="Inactivated Polio (IPV)"
            checked={g17Data.IPV?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'IPV', 'received', e.target.checked)}
          />
        </div>

        {/* Pneumococcal */}
        <div>
          <CheckBox
            text="Pneumococcal Conjugate Vaccine"
            checked={g17Data.Pneumococcal?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'Pneumococcal', 'received', e.target.checked)}
          />
          {g17Data.Pneumococcal?.received && renderDoseCheckboxes('Pneumococcal', ['1.5mos', '2.5mos', '3.5mos'])}
        </div>

        {/* MMR */}
        <div>
          <CheckBox
            text="Measles, Mumps, Rubella (MMR)"
            checked={g17Data.MMR?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'MMR', 'received', e.target.checked)}
          />
          {g17Data.MMR?.received && renderDoseCheckboxes('MMR', ['9mos', '1year'])}
        </div>

        {/* Other */}
        <div>
          <CheckBox
            text="Other vaccines received"
            checked={g17Data.Other?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G17', 'Other', 'received', e.target.checked)}
          />
          {g17Data.Other?.received && (
            <Input
              style={{ marginLeft: '20px', marginTop: '4px', width: 'calc(100% - 20px)' }}
              placeholder="Specify other vaccines"
              value={g17Data.Other?.specify || ''}
              onInput={(e) => handleInfantHealthChange(index, 'G17', 'Other', 'specify', e.target.value.toUpperCase())}
            />
          )}
        </div>
      </div>
    );
};

// G18: Deworming Status (Updated to use handleInfantHealthChange)
const renderDewormingStatusUpdated = (index, entry, handleInfantHealthChange, todayStringForMaxDate) => {
    // Combine data sources into a single object
    const data = {
      ...defaultHealthData.G18,
      ...(entry.G18 || {}),
      // Prioritize new deworming data after fetch.
      ...(entry.dewormingStatus && entry.dewormingDate ? {
        status: entry.dewormingStatus,
        date: entry.dewormingDate
      } : {})
    };
    const dewormingOptions = ["1 Dewormed", "2 Not Dewormed"];


    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <ComboBox
          value={data.status}
          onChange={(e) => handleInfantHealthChange(index, 'G18', 'status', e.target.value)}
        >
          <ComboBoxItem text="" />
          {dewormingOptions.map((opt, i) => (
            <ComboBoxItem key={`g18-opt-${index}-${i}`} text={opt} />
          ))}
        </ComboBox>

        {data.status === '1 Dewormed' && (
          <DatePicker
            value={data.date || ""}
            onChange={(e) => handleInfantHealthChange(index, 'G18', 'date', e.target.value)}
            formatPattern="MM/dd/yyyy"
            placeholder="Date Last Dewormed"
            maxDate={todayStringForMaxDate}
          />
        )}
      </div>
    );
};

// G19: Micronutrient Intake Status (New)
const renderMicronutrientStatusUpdated = (index, entry, handleInfantHealthChange, todayStringForMaxDate) => {
    const parsedMicronutrientData = {}
    const micronutrient_id = {
      VitaminA: 9,
      Iron: 10,
      MNP: 11,
      IronFortifiedRice: 12,
      IodizedSalt: 13,
    };
    if(entry.micronutrientStatus !== undefined && entry.micronutrientDate !== undefined) {
      const micronutrient_date = entry.micronutrientDate;
      const micronutrient_status = entry.micronutrientStatus;
      const datesAndSources = micronutrient_date.split(',');
      const statusIds = micronutrient_status.split(',');
      const nameById = Object.fromEntries(
        Object.entries(micronutrient_id).map(([key, value]) => [value, key])
      );
      statusIds.forEach((id, index) => {
      const name = nameById[id];
      if (name) {
        const micronutrientEntry = { received: true };
        const dateOrSource = datesAndSources[index];

        if (dateOrSource) {
          // The last item for Iodized Salt is a 'source', not a 'date'.
          if (name === "IodizedSalt") {
            micronutrientEntry.source = dateOrSource;
          } else {
            if(dateOrSource !== 'X'){
              micronutrientEntry.date = dateOrSource;
            } 
          }
        }
        parsedMicronutrientData[name] = micronutrientEntry;
      }
    });
    }
    const data = parsedMicronutrientData || entry.G19 || defaultHealthData.G19;
    const saltSourceOptions = [
        "1 Public Market",
        "2 Convenience/Sari-Sari Store",
        "3 Supermarket",
        "Others, specify"
    ]; // Define options locally or from UIModel

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Vitamin A */}
        <div>
          <CheckBox
            text="Vitamin A"
            checked={data.VitaminA?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G19', 'VitaminA', 'received', e.target.checked)}
          />
          {data.VitaminA?.received && (
            <DatePicker
              style={{ marginLeft: '20px', marginTop: '4px', width: 'calc(100% - 20px)' }}
              value={data.VitaminA?.date || ""}
              onChange={(e) => handleInfantHealthChange(index, 'G19', 'VitaminA', 'date', e.target.value)}
              formatPattern="MM/dd/yyyy"
              placeholder="Date Last Received"
              maxDate={todayStringForMaxDate}
            />
          )}
        </div>

        {/* Iron */}
        <div>
          <CheckBox
            text="Iron"
            checked={data.Iron?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G19', 'Iron', 'received', e.target.checked)}
          />
          {data.Iron?.received && (
            <DatePicker
              style={{ marginLeft: '20px', marginTop: '4px', width: 'calc(100% - 20px)' }}
              value={data.Iron?.date || ""}
              onChange={(e) => handleInfantHealthChange(index, 'G19', 'Iron', 'date', e.target.value)}
              formatPattern="MM/dd/yyyy"
              placeholder="Date Last Received"
              maxDate={todayStringForMaxDate}
            />
          )}
        </div>

        {/* MNP */}
        <CheckBox
          text="Micronutrient Powder (MNP)"
          checked={data.MNP?.received || false}
          onChange={(e) => handleInfantHealthChange(index, 'G19', 'MNP', 'received', e.target.checked)}
        />

        {/* Iron-Fortified Rice */}
        <CheckBox
          text="Iron-Fortified Rice"
          checked={data.IronFortifiedRice?.received || false}
          onChange={(e) => handleInfantHealthChange(index, 'G19', 'IronFortifiedRice', 'received', e.target.checked)}
        />

        {/* Iodized Salt */}
        <div>
          <CheckBox
            text="Iodized Salt"
            checked={data.IodizedSalt?.received || false}
            onChange={(e) => handleInfantHealthChange(index, 'G19', 'IodizedSalt', 'received', e.target.checked)}
          />
          {data.IodizedSalt?.received && (
            <div style={{ marginLeft: '20px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <ComboBox
                placeholder="Source of Iodized Salt"
                value={data.IodizedSalt?.source || ""}
                onChange={(e) => handleInfantHealthChange(index, 'G19', 'IodizedSalt', 'source', e.target.value)}
              >
                <ComboBoxItem text="" />
                {saltSourceOptions.map((opt, i) => (
                  <ComboBoxItem key={`g19-salt-opt-${index}-${i}`} text={opt} />
                ))}
              </ComboBox>
              {data.IodizedSalt?.source === 'Others, specify' && (
                <Input
                  placeholder="Specify other source"
                  value={data.IodizedSalt?.sourceOther || ''}
                  onInput={(e) => handleInfantHealthChange(index, 'G19', 'IodizedSalt', 'sourceOther', e.target.value)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
};

// G20: Breastfeeding Status (New)
const renderBreastfeedingStatus = (index, entry, handleInfantHealthChange, todayStringForMaxDate) => {
    let receivedData = {}
    if(entry.breastfeedingStatus !== undefined && entry.breastfeedingDate !== undefined){
      const numbers = entry.breastfeedingStatus.split(',');
      numbers.forEach(num => {
        const monthKey = `Month${num}`;
        receivedData[monthKey] = true;
      });
      receivedData['DateLastBreastfed'] = entry.breastfeedingDate
    }
    
    const data = receivedData || entry.G20 || defaultHealthData.G20;
    const months = ['Month1', 'Month2', 'Month3', 'Month4', 'Month5', 'Month6'];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
         <Label>Exclusive Breastfeeding for:</Label>
         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px' }}>
            {months.map(month => (
                <CheckBox
                    key={`g20-${month}-${index}`}
                    text={month.replace('Month', 'Month ')}
                    checked={data[month] || false}
                    onChange={(e) => handleInfantHealthChange(index, 'G20', month, e.target.checked)}
                />
            ))}
         </div>
         <DatePicker
            value={data.DateLastBreastfed || ""}
            onChange={(e) => handleInfantHealthChange(index, 'G20', 'DateLastBreastfed', e.target.value)}
            formatPattern="MM/dd/yyyy"
            placeholder="Date Last Breastfed"
            maxDate={todayStringForMaxDate}
         />
      </div>
    );
};


export default FamilyDemographic;
